import { IConfigIssue, IEditorPolicy, IEditorRule } from '../model/types';

// Rule limits are positive amounts capped by the backend (the cap follows the largest
// MID limit, Argentina's 1 500 000). The cap applies to min as well, so a min above it
// is named directly instead of surfacing as "max below min".
export const RULE_LIMIT_MIN = 1;
export const RULE_LIMIT_MAX = 1_500_000;

const LIMIT_RANGE = `${RULE_LIMIT_MIN} to ${RULE_LIMIT_MAX.toLocaleString('en-US')}`;

const isLimitValid = (value: number) =>
  Number.isFinite(value) && value >= RULE_LIMIT_MIN && value <= RULE_LIMIT_MAX;

const join = (prefix: string, field: string) => (prefix ? `${prefix}.${field}` : field);

// One rule's own checks (name, limits), keyed under `rulePath` — or by bare field
// name when the rule form validates before the rule exists in the draft.
export const validateRule = (
  rule: Pick<IEditorRule, 'name' | 'min' | 'max'>,
  rulePath = '',
): IConfigIssue[] => {
  const issues: IConfigIssue[] = [];

  if (!rule.name?.trim()) {
    issues.push({ path: join(rulePath, 'name'), message: 'Rule name is required.' });
  }

  const min = Number(rule.min);
  const max = Number(rule.max);
  const minValid = isLimitValid(min);
  const maxValid = isLimitValid(max);

  if (!minValid) {
    issues.push({
      path: join(rulePath, 'min'),
      message: `Min must be a number from ${LIMIT_RANGE}.`,
    });
  }
  if (!maxValid) {
    issues.push({
      path: join(rulePath, 'max'),
      message: `Max must be a number from ${LIMIT_RANGE}.`,
    });
  } else if (minValid && max < min) {
    issues.push({
      path: join(rulePath, 'max'),
      message: 'Max must be greater than or equal to min.',
    });
  }

  return issues;
};

// Mirrors the backend's 422 rules (plus "a policy needs a rule"), keyed by config
// path like the server's issues.
export const validateDraft = (policies: IEditorPolicy[]): IConfigIssue[] => {
  const issues: IConfigIssue[] = [];

  policies.forEach((policy, policyIndex) => {
    const policyPath = `policies.${policyIndex}`;

    if (!policy.name?.trim()) {
      issues.push({ path: `${policyPath}.name`, message: 'Policy name is required.' });
    }

    // Our one structural rule; applies to server-delivered policies too.
    if (!policy.rules.length) {
      issues.push({ path: `${policyPath}.rules`, message: 'A policy needs at least one rule.' });
    }

    policy.rules.forEach((rule, ruleIndex) => {
      issues.push(...validateRule(rule, `${policyPath}.rules.${ruleIndex}`));
    });
  });

  return issues;
};
